'use client';

import { useEffect, useRef, type FocusEvent, type FormEvent } from 'react';
import { FORM_EVENT, trackFormEvent, type TrackedFormVariant } from '@/lib/analytics/posthog';

/** Where a visitor got to in one form instance. */
export interface FormProgress {
  started: boolean;
  attempted: boolean;
  succeeded: boolean;
  /** Field id last focused, never its value. */
  lastField: string | undefined;
  /** An abandon is reported once, however the visitor leaves. */
  reported: boolean;
  /** Field ids clicked or tabbed into, and field ids that received input (typing or autofill). */
  touched: Set<string>;
  typed: Set<string>;
}

export const newFormProgress = (): FormProgress => ({
  started: false,
  attempted: false,
  succeeded: false,
  lastField: undefined,
  reported: false,
  touched: new Set(),
  typed: new Set(),
});

/** Fields that are plumbing, not something a visitor fills in. */
const SKIPPED_FIELDS = new Set(['transport', 'next']);

/**
 * Records a focus: returns the field to report as `form_started` the first
 * time, otherwise undefined. Pure so it can be tested without a DOM.
 */
export function noteFocus(progress: FormProgress, field: string, ignore: ReadonlySet<string>): string | undefined {
  if (!field || SKIPPED_FIELDS.has(field) || ignore.has(field)) return undefined;
  progress.lastField = field;
  progress.touched.add(field);
  if (progress.started) return undefined;
  progress.started = true;
  return field;
}

/** Records that a field received input (typing, paste or a chosen autofill suggestion). */
export function noteInput(progress: FormProgress, field: string, ignore: ReadonlySet<string>): void {
  if (!field || SKIPPED_FIELDS.has(field) || ignore.has(field)) return;
  progress.typed.add(field);
}

/** True when leaving now counts as an abandon that has not been reported yet. */
export function shouldReportAbandon(progress: FormProgress): boolean {
  return progress.started && !progress.succeeded && !progress.reported;
}

/**
 * Tracks form_started (first focus) and form_abandoned (left without a
 * successful submit: page hidden for good, navigated away, or the form
 * unmounted, as when the quote dialog closes). Field ids only, never values.
 */
export function useFormProgressTracking(variant: TrackedFormVariant, ignore: ReadonlySet<string>) {
  const progress = useRef<FormProgress>(newFormProgress());

  useEffect(() => {
    const report = () => {
      const p = progress.current;
      if (!shouldReportAbandon(p)) return;
      p.reported = true;
      trackFormEvent(FORM_EVENT.abandoned, { variant, attempted: p.attempted, ...(p.lastField ? { field: p.lastField } : {}) });
    };
    window.addEventListener('pagehide', report);
    return () => {
      window.removeEventListener('pagehide', report);
      report();
    };
  }, [variant]);

  return {
    onFocusCapture: (event: FocusEvent<HTMLFormElement>) => {
      const field = event.target instanceof HTMLElement ? (event.target.getAttribute('name') ?? '') : '';
      const first = noteFocus(progress.current, field, ignore);
      if (first) trackFormEvent(FORM_EVENT.started, { variant, field: first });
    },
    onInputCapture: (event: FormEvent<HTMLFormElement>) => {
      const field = event.target instanceof HTMLElement ? (event.target.getAttribute('name') ?? '') : '';
      noteInput(progress.current, field, ignore);
    },
    /** Which fields were clicked into and which received input, for a failed-submit report. */
    activity: () => ({ touched: [...progress.current.touched], typed: [...progress.current.typed] }),
    markAttempted: () => {
      progress.current.attempted = true;
    },
    markSucceeded: () => {
      progress.current.succeeded = true;
    },
  };
}
